import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { BROAD_LABELS, THEME_LABELS } from '@/lib/company-qualification';
import { pageIsStale } from '@/lib/company-restitution';
import {
  type CompanyQualificationSheet,
  QUALIFICATION_SECTIONS,
  QUALIFICATION_THEMES,
  type QualificationThemeKey,
} from '@shared/types/company-qualification';

interface Props {
  sheet: CompanyQualificationSheet;
  onOpen: (theme: QualificationThemeKey) => void;
  /** The « Qualification » card: the whole sheet, its state and the working hypotheses. */
  onOpenSheet: () => void;
}

const CARD = 'flex min-h-[8.5rem] flex-col rounded-xl border border-domino-cardBorder bg-domino-warm px-4 py-3 text-left transition-colors hover:border-domino-aubergine/50 hover:bg-white';

/** The first card: the qualification as a whole, opening the detailed sheet. */
const SheetCard = ({ sheet, onOpen }: { sheet: CompanyQualificationSheet; onOpen: () => void }) => {
  const lines = QUALIFICATION_SECTIONS.reduce((total, key) => total + sheet.sections[key].length, 0);
  const hypotheses = sheet.hypotheses.length;
  return (
    <button type="button" onClick={onOpen} className={CARD}>
      <span className="text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">Qualification</span>
      <span className="mt-1.5 text-sm font-medium leading-6 text-domino-ink">{BROAD_LABELS[sheet.broad.state]}</span>
      <span className="mt-auto flex items-center justify-between gap-2 pt-2 text-xs">
        <span className="text-domino-inkSoft">
          {lines} constat{lines > 1 ? 's' : ''}{hypotheses ? ` · ${hypotheses} hypothèse${hypotheses > 1 ? 's' : ''}` : ''}
        </span>
        <span className="font-medium text-domino-aubergine">Voir la fiche →</span>
      </span>
    </button>
  );
};

/** The qualification card, then one card per theme: what it found (the page's conclusion once written), its state; a click opens the theme. */
export const ThemeCards = ({ sheet, onOpen, onOpenSheet }: Props) => (
  // Columns follow the room left (the analyst's chat may take the left of the screen).
  <section aria-label="Thèmes" className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-3">
    <SheetCard sheet={sheet} onOpen={onOpenSheet} />
    {QUALIFICATION_THEMES.map(({ key, sections }) => {
      const lines = sections.reduce((total, section) => total + sheet.sections[section].length, 0);
      const page = sheet.restitution.pages[key];
      const deep = sheet.themes[key].depth === 'deep';
      return (
        <button
          key={key}
          type="button"
          onClick={() => onOpen(key)}
          className={CARD}
        >
          <span className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-domino-inkSoft">{THEME_LABELS[key].label}</span>
            {deep && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-700" aria-label="Approfondi" />}
          </span>
          <span className={cn('mt-1.5 line-clamp-3 text-sm leading-6', page ? 'font-medium text-domino-ink' : 'text-domino-inkSoft')}>
            {page ? page.title : THEME_LABELS[key].deliverable}
          </span>
          <span className="mt-auto flex items-center justify-between gap-2 pt-2 text-xs">
            <span className="text-domino-inkSoft">
              {lines ? `${lines} constat${lines > 1 ? 's' : ''}` : 'Rien encore'}
              {page && pageIsStale(sheet, key) ? ' · a changé' : ''}
            </span>
            <span className="font-medium text-domino-aubergine">{page ? 'Voir' : lines ? 'Ouvrir' : 'Commencer'} →</span>
          </span>
        </button>
      );
    })}
  </section>
);
